import { basename, extname, isAbsolute, join } from "node:path";
import { MAX_FILE_SIZE_BYTES } from "@project/shared";
import { uploadAttachmentContract } from "@project/shared/tool-contracts";
import { defineContractTool } from "./contract-tool.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import { readWorkspaceBytes, statWorkspacePath } from "../workbench/fs.js";
import { textResult } from "./helpers.js";

// Any file type is accepted so agents deliver docs, notes, and data by
// attaching them to the card — never by publishing an off-platform Claude
// artifact. Mirrors conveyor-mcp's map (packages/conveyor-mcp/src/tools/
// attachments.ts); unknown extensions fall back to application/octet-stream.
const MIME_BY_EXT: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".svg": "image/svg+xml",
  ".txt": "text/plain",
  ".log": "text/plain",
  ".md": "text/markdown",
  ".mmd": "text/vnd.mermaid",
  ".mermaid": "text/vnd.mermaid",
  ".csv": "text/csv",
  ".html": "text/html",
  ".css": "text/css",
  ".js": "text/javascript",
  ".ts": "text/plain",
  ".json": "application/json",
  ".yaml": "application/yaml",
  ".yml": "application/yaml",
  ".pdf": "application/pdf",
  ".zip": "application/zip",
  ".gz": "application/gzip",
  ".mp4": "video/mp4",
  ".webm": "video/webm",
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
};

function inferMimeType(filePath: string): string {
  return MIME_BY_EXT[extname(filePath).toLowerCase()] ?? "application/octet-stream";
}

/**
 * Report which glossary tags the file was labelled with. An unmatched name is
 * surfaced rather than swallowed: the upload still succeeded, and the agent
 * needs to know the label did not stick so it can retry with the real name.
 */
function describeTags(applied?: string[], unknown?: string[]): string {
  const parts: string[] = [];
  if (applied?.length) parts.push(` Tagged: ${applied.join(", ")}.`);
  if (unknown?.length) {
    parts.push(` No tag matched: ${unknown.join(", ")} — check the project's tag names.`);
  }
  return parts.join("");
}

// Extensions GitHub renders inline from an external URL. `.svg` is deliberately
// absent: GitHub serves markdown images through its camo image proxy, and camo
// refuses `image/svg+xml`, so an `![…](…)` embed of an SVG shows as broken. An
// SVG capture still gets a plain link, which reviewers can open.
const EMBEDDABLE_IN_MARKDOWN = new Set([".png", ".jpg", ".jpeg", ".gif", ".webp"]);

/**
 * Hand the capability download URL back to the agent so visual proof can go in
 * the PR description as well as on the card. `confirmFileUpload` has always
 * returned it (an HMAC-signed URL our own API streams bytes for — it renders in
 * an `<img>`, unlike a Bearer-authed endpoint), but this tool used to drop it,
 * which is why the mode prompt long claimed there was "no programmatic path" to
 * put an image in a PR. Images get a ready-to-paste markdown line; video and
 * other types get a plain link, since GitHub does not render those inline.
 */
function describeEmbed(fileName: string, downloadUrl?: string): string {
  if (!downloadUrl) return "";
  const isImage = EMBEDDABLE_IN_MARKDOWN.has(extname(fileName).toLowerCase());
  const snippet = isImage ? `![${fileName}](${downloadUrl})` : `[${fileName}](${downloadUrl})`;
  return `\n\nPaste this into the PR description to show it inline:\n${snippet}`;
}

export function buildUploadAttachmentTool(connection: AgentConnection, config: AgentRunnerConfig) {
  return defineContractTool(uploadAttachmentContract, async ({ path, title, tags }) => {
    try {
      const filePath = isAbsolute(path) ? path : join(config.workspaceDir, path);
      const mimeType = inferMimeType(filePath);

      // Route through the workspace-fs proxy so split-mode pods can see files
      // Claude Code wrote into the workbench container — a plain node:fs stat
      // here runs in the agent container and misses them ("File not found" for
      // a screenshot that demonstrably exists on the Bash filesystem).
      const info = await statWorkspacePath(filePath);
      if (!info.isFile) {
        return textResult(`File not found: ${filePath}`);
      }
      if (info.size > MAX_FILE_SIZE_BYTES) {
        return textResult(
          `File is ${info.size} bytes — exceeds the ${MAX_FILE_SIZE_BYTES} byte (25MB) upload limit.`,
        );
      }

      const fileName = basename(filePath);
      const { fileId, uploadUrl } = await connection.call("requestFileUpload", {
        sessionId: connection.sessionId,
        fileName,
        mimeType,
        fileSize: info.size,
      });

      const bytes = await readWorkspaceBytes(filePath);
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": mimeType },
        // Normalize to a plain Uint8Array: the workbench proxy hands back a
        // Buffer<ArrayBufferLike> that newer @types/node rejects as a BodyInit.
        body: new Uint8Array(bytes),
      });
      if (!res.ok) {
        return textResult(
          `Upload to storage failed: HTTP ${res.status} ${await res.text().catch(() => "")}`,
        );
      }

      const result = await connection.call("confirmFileUpload", {
        sessionId: connection.sessionId,
        fileId,
        title,
        tags,
      });

      return textResult(
        `Uploaded ${fileName} (${info.size} bytes) and posted it to the task chat${title ? ` with caption "${title}"` : ""}. File ID: ${result.fileId}${describeTags(result.appliedTags, result.unknownTags)}${describeEmbed(fileName, result.downloadUrl)}`,
      );
    } catch (error) {
      return textResult(
        `Failed to upload attachment: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  });
}
